import { TestCustomerService } from './../test-customer/test-customer.service';
import { AuthenticationService } from './../authentication/authentication.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '../config';
import {
  Observable,
  Subject,
  map,
  throwError,
  catchError,
  tap,
  of,
} from 'rxjs';
import { BackendResponse } from '../../types/interfaces';
import {
  Customer,
  DetailedCustomer,
  IbanInUseCheck,
  ResidentsIban,
} from '../../../shared/models/customer.model';
import { deserializeResponse, replaceUrlParams } from '../utils/helpers';
import { sortResidents } from '../../../shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../utils/snackbar.service';
import { ResponseStatus } from '../../types/enums';

@Injectable({
  providedIn: 'root',
})
export class CustomersService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private testCustomerService: TestCustomerService
  ) {}

  getCustomers(): Observable<BackendResponse<Customer[]>> {
    const url = this.configService.endpoints.customers.getCustomers;
    return this.http.get<BackendResponse<Customer[]>>(url).pipe(
      map((arg: any) => deserializeResponse<Customer[]>(arg, Customer)),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('customers.error.inCustomersRetrieving')
        );
        return throwError(() => error);
      })
    );
  }

  getCustomer(
    customerId: number
  ): Observable<BackendResponse<DetailedCustomer>> {
    if (
      this.testCustomerService.isLoggedInAsTestCustomer() &&
      this.testCustomerService.hasCustomer()
    ) {
      return of({
        status: ResponseStatus.Success,
        message: 'Test customer loaded successfully',
        data: this.testCustomerService.loadCustomer(),
      });
    }
    const url = replaceUrlParams(
      this.configService.endpoints.customers.getCustomer,
      ['customerId'],
      [customerId]
    );
    return this.http.get<BackendResponse<DetailedCustomer>>(url).pipe(
      map((arg: any) =>
        deserializeResponse<DetailedCustomer>(arg, DetailedCustomer)
      ),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('customers.error.inCustomerRetrieving')
        );
        return throwError(() => error);
      }),
      tap(customer => {
        if (this.testCustomerService.isLoggedInAsTestCustomer()) {
          this.testCustomerService.saveCustomer(customer.data);
        }
      })
    );
  }

  setCustomer(customerId: number) {
    const url = this.configService.endpoints.customers.setCustomer;
    return this.http
      .post<BackendResponse<{ token: string }>>(url, { customerId })
      .pipe(
        catchError(error => {
          this.snackbarService.error(
            this.translateService.instant('customers.error.inCustomerSelecting')
          );
          return throwError(() => error);
        })
      );
  }
  getBankInfoByResidentId(
    customerId: number,
    residentId: number
  ): Observable<BackendResponse<ResidentsIban>> {
    const url = replaceUrlParams(
      this.configService.endpoints.customers.bankInfoByResidentId,
      ['customerId', 'residentId'],
      [customerId, residentId]
    );
    return this.http.get<BackendResponse<ResidentsIban>>(url).pipe(
      map((arg: any) => deserializeResponse<ResidentsIban>(arg, ResidentsIban)),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('sepa.error.inSepaRetrieving')
        );
        return throwError(() => error);
      })
    );
  }

  saveBankInfoByResidentId(
    customerId: number,
    residentId: number,
    body: ResidentsIban
  ): Observable<BackendResponse<ResidentsIban>> {
    if (
      this.testCustomerService.isLoggedInAsTestCustomer() &&
      this.testCustomerService.hasCustomer()
    ) {
      return of({
        status: ResponseStatus.Success,
        message: 'Resident bank info saved successfully',
        data: body,
      });
    }
    const url = replaceUrlParams(
      this.configService.endpoints.customers.bankInfoByResidentId,
      ['customerId', 'residentId'],
      [customerId, residentId]
    );
    return this.http.post<BackendResponse<ResidentsIban>>(url, body).pipe(
      map((arg: any) => deserializeResponse<ResidentsIban>(arg, ResidentsIban)),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant('sepa.error.inSepaSaving')
        );
        return throwError(() => error);
      })
    );
  }

  checkIbanInUseByResidentId(
    customerId: number,
    residentId: number,
    iban: string
  ): Observable<boolean> {
    if (
      this.testCustomerService.isLoggedInAsTestCustomer() &&
      this.testCustomerService.hasCustomer()
    ) {
      return of(false);
    }
    const url = replaceUrlParams(
      this.configService.endpoints.customers.checkIbanInUseByResidentId,
      ['customerId', 'residentId'],
      [customerId, residentId]
    );
    return this.http.post<BackendResponse<IbanInUseCheck>>(url, { iban }).pipe(
      map(res => res.data.inUse),
      catchError(error => {
        this.snackbarService.error(
          this.translateService.instant('sepa.error.inIbanCheck')
        );
        return throwError(() => error);
      })
    );
  }

  updateSepaEmailByResidentId(
    customerId: number,
    residentId: number,
    sepaEmail: string
  ): Observable<BackendResponse<{ sepaEmail: string }>> {
    if (
      this.testCustomerService.isLoggedInAsTestCustomer() &&
      this.testCustomerService.hasCustomer()
    ) {
      return of({
        status: ResponseStatus.Success,
        message: 'Resident SEPA email updated successfully',
        data: { sepaEmail },
      });
    }
    const url = replaceUrlParams(
      this.configService.endpoints.customers.updateSepaEmailByResidentId,
      ['customerId', 'residentId'],
      [customerId, residentId]
    );
    return this.http
      .put<BackendResponse<{ sepaEmail: string }>>(url, { sepaEmail })
      .pipe(
        catchError(error => {
          if (error.isHandled) return;
          this.snackbarService.error(
            this.translateService.instant('sepa.error.inSepaSaving')
          );
          return throwError(() => error);
        })
      );
  }

  deleteBankInfoByResidentId(
    customerId: number,
    residentId: number,
    residentName: string
  ): Observable<any> {
    const url = replaceUrlParams(
      this.configService.endpoints.customers.bankInfoByResidentId,
      ['customerId', 'residentId'],
      [customerId, residentId]
    );
    return this.http.delete(url).pipe(
      map(res => res),
      catchError(error => {
        if (error.isHandled) return;
        if (error.status === 409) {
          this.snackbarService.error(
            `Leider können Sie die IBAN Daten von ${residentName} aktuell nicht löschen, da sich im System noch eine offene Bestellung für diese Person befindet. Bitte versuchen Sie es zu einem späteren Zeitpunkt noch einmal.`
          );
          return throwError(() => error);
        }
        this.snackbarService.error(
          this.translateService.instant('sepa.error.inSepaDeleting')
        );
        return throwError(() => error);
      })
    );
  }
}
